import React, { useState, useEffect, useCallback } from 'react';
import { ListGroup, Spinner, Alert, Form } from 'react-bootstrap';
import { FaFileAlt, FaTrash, FaUpload, FaDownload, FaEdit, FaCheck, FaTimes } from 'react-icons/fa';
import { apiFetch, mediaUrl } from '../api';
import { useAuth } from '../auth/AuthContext';
import { canUploadFiles } from '../auth/roles';
import ConfirmDeleteModal from './ConfirmDeleteModal';

function formatSize(bytes) {
  if (bytes === null || bytes === undefined) return '';
  const units = ['Б', 'КБ', 'МБ', 'ГБ'];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(i === 0 || value >= 10 ? 0 : 1)} ${units[i]}`;
}

function ProductFiles({ productId }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const { user } = useAuth();
  const canUpload = canUploadFiles(user?.role);

  const fetchFiles = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    apiFetch(`/api/product-files/?product=${productId}`)
      .then((data) => {
        setFiles(data.results || data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError('Не удалось загрузить список файлов');
        setLoading(false);
      });
  }, [productId]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleUpload = (e) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length === 0) return;
    setUploading(true);
    setError(null);

    const uploadOne = (file) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('product', productId);
      return apiFetch('/api/product-files/', { method: 'POST', body: formData });
    };

    Promise.all(selected.map(uploadOne))
      .then(() => fetchFiles(true))
      .catch((err) => {
        console.error(err);
        setError(err.message || 'Не удалось загрузить файлы');
      })
      .finally(() => {
        setUploading(false);
        e.target.value = '';
      });
  };

  const startEdit = (file) => {
    setEditingId(file.id);
    setEditName(file.original_name || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const saveEdit = async (fileId) => {
    const trimmed = editName.trim();
    if (!trimmed) {
      setError('Название не может быть пустым');
      return;
    }
    try {
      await apiFetch(`/api/product-files/${fileId}/`, {
        method: 'PATCH',
        body: JSON.stringify({ original_name: trimmed }),
      });
      await fetchFiles(true);
      setEditingId(null);
    } catch (err) {
      setError(err.message || 'Не удалось переименовать файл');
    }
  };

  const handleKeyDown = (e, fileId) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEdit(fileId);
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  return (
    <div className="app-card mb-4">
      <div className="app-card-header d-flex justify-content-between align-items-center">
        <span>📎 Файлы изделия</span>
        {canUpload && (
          <div>
            <label htmlFor={`file-upload-${productId}`} className="app-btn app-btn-primary mb-0" style={{ cursor: 'pointer' }}>
              <FaUpload /> {uploading ? 'Загрузка...' : 'Добавить'}
            </label>
            <input
              id={`file-upload-${productId}`}
              type="file"
              multiple
              onChange={handleUpload}
              disabled={uploading}
              style={{ display: 'none' }}
            />
          </div>
        )}
      </div>
      <div className="app-card-body">
        {!canUpload && (
          <div className="app-text-muted small mb-2">
            Загрузка файлов доступна для менеджера, администратора и сотрудников отделов.
          </div>
        )}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {loading ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        ) : files.length === 0 ? (
          <div className="app-text-muted small">Файлов пока нет</div>
        ) : (
          <ListGroup variant="flush">
            {files.map((f) => (
              <ListGroup.Item
                key={f.id}
                className="d-flex justify-content-between align-items-center flex-wrap gap-2 px-0"
                style={{ background: 'transparent', borderColor: 'var(--surface-border)' }}
              >
                <div className="d-flex align-items-center gap-2" style={{ minWidth: 0, flex: 1 }}>
                  <FaFileAlt className="app-text-muted" />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    {editingId === f.id ? (
                      <div className="d-flex align-items-center gap-2">
                        <Form.Control
                          type="text"
                          size="sm"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onBlur={() => saveEdit(f.id)}
                          onKeyDown={(e) => handleKeyDown(e, f.id)}
                          autoFocus
                          style={{ maxWidth: 300 }}
                        />
                        <FaCheck
                          className="app-text-muted"
                          style={{ cursor: 'pointer' }}
                          onClick={() => saveEdit(f.id)}
                        />
                        <FaTimes
                          className="app-text-muted"
                          style={{ cursor: 'pointer' }}
                          onClick={cancelEdit}
                        />
                      </div>
                    ) : (
                      <div>
                        <div className="text-truncate" style={{ maxWidth: 220 }}>
                          {f.original_name || (f.file || '').split('/').pop()}
                        </div>
                        <div className="app-text-muted small">{formatSize(f.size)}</div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="d-flex gap-2">
                  {canUpload && editingId !== f.id && (
                    <button
                      type="button"
                      className="app-btn app-btn-sm"
                      onClick={() => startEdit(f)}
                      title="Переименовать"
                    >
                      <FaEdit />
                    </button>
                  )}
                  <a
                    href={mediaUrl(f.file)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="app-btn app-btn-sm"
                  >
                    <FaDownload />
                  </a>
                  {canUpload && (
                    <button
                      type="button"
                      className="app-btn app-btn-sm app-btn-danger-outline"
                      onClick={() => setDeleteTarget(f)}
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </div>

      <ConfirmDeleteModal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        onConfirm={async () => {
          await apiFetch(`/api/product-files/${deleteTarget.id}/`, { method: 'DELETE' });
          setDeleteTarget(null);
          fetchFiles(true);
        }}
        title="Удалить файл?"
        message="Файл будет удалён без возможности восстановления."
        itemName={deleteTarget?.original_name || deleteTarget?.file?.split('/').pop()}
      />
    </div>
  );
}

export default ProductFiles;